import React, { memo } from 'react';
import { motion } from 'framer-motion';
import {
  FaShoppingBag,
  FaArrowLeft,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaChartLine,
} from 'react-icons/fa';
import { Order } from '../../types/order';
import { formatPrice } from '../../utils/orderUtils';

interface UserOrdersHeaderProps {
  onGoHome: () => void;
  totalOrdersCount: number;
  orders: Order[];
}

export const UserOrdersHeader = memo<UserOrdersHeaderProps>(
  ({ onGoHome, totalOrdersCount, orders }) => {
    // İstatistikleri hesapla
    const totalSpent = orders.reduce((sum, order) => sum + (order.total_amount || 0), 0);

    // Bu ay harcanan miktar
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const thisMonthSpent = orders
      .filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate.getMonth() === currentMonth && orderDate.getFullYear() === currentYear;
      })
      .reduce((sum, order) => sum + (order.total_amount || 0), 0);

    // Tamamlanan siparişler
    const completedOrders = orders.filter(order => order.status === 'completed').length;

    return (
      <div className="mb-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="flex items-center justify-between mb-6"
        >
          <div className="flex items-center gap-6">
            <button
              onClick={onGoHome}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors p-2 hover:bg-white/70 rounded-lg"
            >
              <FaArrowLeft />
              <span className="font-medium">Ana Sayfa</span>
            </button>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-r from-green-500 to-green-400 rounded-xl shadow-lg">
                <FaShoppingBag className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Siparişlerim</h1>
                <p className="text-gray-600">Sipariş geçmişinizi görüntüleyin ve yönetin</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* İstatistik Kartları */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {/* Toplam Sipariş */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/50 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FaShoppingBag className="text-blue-600 text-lg" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Toplam Sipariş</p>
                <p className="text-xl font-bold text-gray-800">{totalOrdersCount}</p>
              </div>
            </div>
          </div>

          {/* Toplam Harcama */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/50 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FaMoneyBillWave className="text-green-600 text-lg" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Toplam Harcama</p>
                <p className="text-xl font-bold text-green-600">{formatPrice(totalSpent)}</p>
              </div>
            </div>
          </div>

          {/* Bu Ay Harcama */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/50 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FaCalendarAlt className="text-purple-600 text-lg" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Bu Ay</p>
                <p className="text-xl font-bold text-purple-600">{formatPrice(thisMonthSpent)}</p>
              </div>
            </div>
          </div>

          {/* Tamamlanan Siparişler */}
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/50 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <FaChartLine className="text-orange-600 text-lg" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Tamamlanan</p>
                <p className="text-xl font-bold text-orange-600">{completedOrders}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }
);

UserOrdersHeader.displayName = 'UserOrdersHeader';
